import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useBranch } from "../contexts/BranchContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLang } from "../contexts/LanguageContext";
import { MapPin, Phone, Clock, Menu, ChevronDown, Instagram, Sun, Moon, Globe, Youtube, Facebook } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { motion, AnimatePresence } from "framer-motion";

const navRoutes = [
  { to: "/", key: "home" },
  { to: "/menu", key: "menu" },
  { to: "/galeri", key: "gallery" },
  { to: "/hakkimizda", key: "about" },
  { to: "/franchise", key: "franchise" },
];

const branchLabels = { caddebostan: "Caddebostan", zeytinburnu: "Zeytinburnu" };

const socialLinks = {
  instagram: {
    caddebostan: "https://www.instagram.com/tepsietcaddebostan?igsh=MWUybnBtbXl6bmIzMA==",
    zeytinburnu: "https://www.instagram.com/tepsiet?igsh=MWdvZzNkeHgyMHd6YQ==",
  },
  youtube: "https://www.youtube.com/channel/UCZfcdnsbDTagmzMc_djQBPg",
  facebook: "https://www.facebook.com/tepsiet/",
};

const getirLinks = {
  caddebostan: "https://getir.com/yemek/restoran/tepsi-et-ocakbasi-kadikoy-caddebostan-mah-kadikoy-istanbul/",
  zeytinburnu: "https://getir.com/yemek/restoran/tepsi-et-seyitnizam-mah-zeytinburnu-istanbul/",
};

const langLabels = { tr: "TR", en: "EN", ar: "AR" };
const langNames = { tr: "Türkçe", en: "English", ar: "العربية" };

export default function Layout({ children }) {
  const { selectedBranch, setSelectedBranch } = useBranch();
  const { theme, toggleTheme } = useTheme();
  const { lang, setLang, t } = useLang();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isDark = theme === "dark";
  const bg = isDark ? "bg-[#0a0a0a]" : "bg-[#FAFAF8]";
  const text = isDark ? "text-[#F5F5F5]" : "text-[#1A1A1A]";
  const textMuted = isDark ? "text-[#999]" : "text-[#666]";
  const textLight = isDark ? "text-[#E5E5E5]" : "text-[#333]";
  const borderSubtle = isDark ? "border-white/5" : "border-black/5";
  const cardBg = isDark ? "bg-[#121212]" : "bg-white";
  const navBg = isDark ? "rgba(10,10,10,0.92)" : "rgba(250,250,248,0.92)";

  return (
    <div className={`min-h-screen ${bg} ${text} transition-colors duration-300`} dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 border-b border-[#8B1A1A]/20`}
        style={{ backgroundColor: navBg, backdropFilter: "blur(16px)" }}
      >
        <div className="max-w-[1400px] mx-auto px-6 flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <span className="font-heading text-2xl md:text-3xl font-bold text-[#8B1A1A] tracking-tight transition-colors group-hover:text-[#A52525]">
              TEPSİ ET
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navRoutes.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.to.replace("/", "") || "home"}`}
                className={`font-body text-sm uppercase tracking-[0.2em] transition-colors duration-300 hover:text-[#8B1A1A] ${
                  location.pathname === link.to ? "text-[#8B1A1A]" : textLight
                }`}
              >
                {t.nav[link.key]}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="language-selector"
                className="flex items-center gap-1.5 border border-[#8B1A1A]/30 px-3 py-2 text-xs font-body uppercase tracking-widest text-[#8B1A1A] hover:bg-[#8B1A1A] hover:text-white transition-all duration-300"
              >
                <Globe className="w-3.5 h-3.5" />
                <span>{langLabels[lang]}</span>
                <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className={`${cardBg} border-[#8B1A1A]/20`}>
                {Object.keys(langLabels).map((l) => (
                  <DropdownMenuItem
                    key={l}
                    data-testid={`lang-${l}`}
                    onClick={() => setLang(l)}
                    className={`cursor-pointer font-body tracking-wide ${
                      lang === l ? "text-[#8B1A1A]" : textLight
                    } hover:text-[#8B1A1A] focus:text-[#8B1A1A]`}
                  >
                    {langNames[l]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className={`w-9 h-9 flex items-center justify-center border border-[#8B1A1A]/30 text-[#8B1A1A] hover:bg-[#8B1A1A] hover:text-white transition-all duration-300`}
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {/* Branch Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="branch-selector"
                className="flex items-center gap-2 border border-[#8B1A1A]/40 px-4 py-2 text-sm font-body uppercase tracking-widest text-[#8B1A1A] hover:bg-[#8B1A1A] hover:text-white transition-all duration-300"
              >
                <MapPin className="w-4 h-4" />
                <span className="hidden sm:inline">{branchLabels[selectedBranch]}</span>
                <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className={`${cardBg} border-[#8B1A1A]/20`}>
                <DropdownMenuItem
                  data-testid="branch-zeytinburnu"
                  onClick={() => setSelectedBranch("zeytinburnu")}
                  className={`cursor-pointer font-body tracking-wide ${
                    selectedBranch === "zeytinburnu" ? "text-[#8B1A1A]" : textLight
                  } hover:text-[#8B1A1A] focus:text-[#8B1A1A]`}
                >
                  <MapPin className="w-4 h-4 mr-2" /> Zeytinburnu
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="branch-caddebostan"
                  onClick={() => setSelectedBranch("caddebostan")}
                  className={`cursor-pointer font-body tracking-wide ${
                    selectedBranch === "caddebostan" ? "text-[#8B1A1A]" : textLight
                  } hover:text-[#8B1A1A] focus:text-[#8B1A1A]`}
                >
                  <MapPin className="w-4 h-4 mr-2" /> Caddebostan
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger className="md:hidden text-[#8B1A1A]" data-testid="mobile-menu-toggle">
                <Menu className="w-6 h-6" />
              </SheetTrigger>
              <SheetContent side={lang === "ar" ? "left" : "right"} className={`${bg} border-l border-[#8B1A1A]/20 w-72`}>
                <SheetTitle className="text-[#8B1A1A] font-heading text-xl mb-8">TEPSİ ET</SheetTitle>
                <div className="flex flex-col gap-6">
                  {navRoutes.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setMobileOpen(false)}
                      className={`font-body text-lg uppercase tracking-[0.15em] transition-colors hover:text-[#8B1A1A] ${
                        location.pathname === link.to ? "text-[#8B1A1A]" : textLight
                      }`}
                    >
                      {t.nav[link.key]}
                    </Link>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      {/* Main */}
      <main className="pt-16 md:pt-20">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname + selectedBranch}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className={`border-t border-[#8B1A1A]/10 ${bg}`} data-testid="site-footer">
        <div className="max-w-[1400px] mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
            {/* Brand */}
            <div className="lg:col-span-2">
              <h3 className="font-heading text-2xl text-[#8B1A1A] mb-4">TEPSİ ET</h3>
              <p className={`font-body text-sm ${textMuted} leading-relaxed mb-6`}>
                {t.footer.desc}
              </p>
              <div className="flex gap-3">
                <a href={socialLinks.instagram.zeytinburnu} target="_blank" rel="noopener noreferrer" className={`w-9 h-9 flex items-center justify-center border ${borderSubtle} ${textMuted} hover:text-[#8B1A1A] hover:border-[#8B1A1A]/30 transition-all`} data-testid="social-instagram-zeytin">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href={socialLinks.instagram.caddebostan} target="_blank" rel="noopener noreferrer" className={`w-9 h-9 flex items-center justify-center border ${borderSubtle} ${textMuted} hover:text-[#8B1A1A] hover:border-[#8B1A1A]/30 transition-all`} data-testid="social-instagram-cadde">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className={`w-9 h-9 flex items-center justify-center border ${borderSubtle} ${textMuted} hover:text-[#8B1A1A] hover:border-[#8B1A1A]/30 transition-all`} data-testid="social-youtube">
                  <Youtube className="w-4 h-4" />
                </a>
                <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className={`w-9 h-9 flex items-center justify-center border ${borderSubtle} ${textMuted} hover:text-[#8B1A1A] hover:border-[#8B1A1A]/30 transition-all`} data-testid="social-facebook">
                  <Facebook className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Zeytinburnu */}
            <div>
              <h4 className="font-heading text-lg text-[#8B1A1A] mb-4">Zeytinburnu</h4>
              <div className={`space-y-3 font-body text-sm ${textMuted}`}>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-[#8B1A1A] mt-0.5 shrink-0" />
                  <span>Seyitnizam, Mevlana Cd. No:81-83/D, 34015 Zeytinburnu/İstanbul</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#8B1A1A] shrink-0" />
                  <span>0(553) 889 01 46</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#8B1A1A] shrink-0" />
                  <span>{t.branches.zeytin.hours}</span>
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <a href={getirLinks.zeytinburnu} target="_blank" rel="noopener noreferrer" className="inline-flex items-center hover:opacity-80 transition-opacity" data-testid="getir-zeytinburnu">
                    <img src="/assets/getir.png" alt="Getir Yemek" className="h-8 w-auto" />
                  </a>
                  <a href="https://tgoyemek.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center hover:opacity-80 transition-opacity" data-testid="trendyol-zeytinburnu">
                    <img src="/assets/trendyol.png" alt="Trendyol Yemek" className="h-8 w-auto" />
                  </a>
                  <a href="https://fuudy.co" target="_blank" rel="noopener noreferrer" className="inline-flex items-center hover:opacity-80 transition-opacity" data-testid="fuudy-zeytinburnu">
                    <img src="/assets/fuudy.png" alt="Fuudy" className="h-8 w-auto" />
                  </a>
                </div>
                <a href="https://www.yemeksepeti.com/restaurant/iibz/tepsi-et-iibz" target="_blank" rel="noopener noreferrer" className="inline-flex items-center hover:opacity-80 transition-opacity mt-1" data-testid="yemeksepeti-zeytinburnu">
                  <img src="/assets/yemeksepeti.jpg" alt="Yemeksepeti" className="h-6 w-auto" />
                </a>
              </div>
            </div>

            {/* Caddebostan */}
            <div>
              <h4 className="font-heading text-lg text-[#8B1A1A] mb-4">Caddebostan</h4>
              <div className={`space-y-3 font-body text-sm ${textMuted}`}>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-[#8B1A1A] mt-0.5 shrink-0" />
                  <span>Caddebostan, Yener Sk. No:5 D:1a, 34728 Kadıköy/İstanbul</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#8B1A1A] shrink-0" />
                  <span>0(506) 382 46 46</span>
                </div>
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-[#8B1A1A] mt-0.5 shrink-0" />
                  <span>{t.branches.cadde.hours}</span>
                </div>
                <a href={getirLinks.caddebostan} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity mt-1" data-testid="getir-caddebostan">
                  <img src="/assets/getir.png" alt="Getir Yemek" className="h-8 w-auto" />
                </a>
              </div>
            </div>

            {/* Navigation */}
            <div>
              <h4 className="font-heading text-lg text-[#8B1A1A] mb-4">{t.footer.explore}</h4>
              <div className="flex flex-col gap-3">
                {navRoutes.map((link) => (
                  <Link key={link.to} to={link.to} className={`font-body text-sm ${textMuted} hover:text-[#8B1A1A] transition-colors`}>
                    {t.nav[link.key]}
                  </Link>
                ))}
              </div>
              <div className={`mt-6 pt-4 border-t ${borderSubtle}`}>
                <p className={`font-body text-xs ${textMuted} uppercase tracking-wider mb-2`}>{t.footer.followUs}</p>
                <div className="flex flex-col gap-2">
                  <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className={`font-body text-sm ${textMuted} hover:text-[#8B1A1A] transition-colors inline-flex items-center gap-2`}>
                    <Youtube className="w-3.5 h-3.5" /> YouTube
                  </a>
                  <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className={`font-body text-sm ${textMuted} hover:text-[#8B1A1A] transition-colors inline-flex items-center gap-2`}>
                    <Facebook className="w-3.5 h-3.5" /> Facebook
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className={`mt-16 pt-8 border-t ${borderSubtle} flex flex-col sm:flex-row items-center justify-between gap-4`}>
            <p className={`font-body text-xs ${isDark ? "text-[#666]" : "text-[#aaa]"} tracking-wider uppercase`}>
              &copy; 2025 Tepsi Et. {t.footer.rights}
            </p>
            <div className="flex items-center gap-4">
              <a href={getirLinks.zeytinburnu} target="_blank" rel="noopener noreferrer" className={`font-body text-xs ${isDark ? "text-[#666]" : "text-[#aaa]"} hover:text-[#8B1A1A] transition-colors uppercase tracking-wider`}>
                {t.footer.orderZeytin}
              </a>
              <span className={`${isDark ? "text-[#333]" : "text-[#ccc]"}`}>|</span>
              <a href={getirLinks.caddebostan} target="_blank" rel="noopener noreferrer" className={`font-body text-xs ${isDark ? "text-[#666]" : "text-[#aaa]"} hover:text-[#8B1A1A] transition-colors uppercase tracking-wider`}>
                {t.footer.orderCadde}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
